import React, { ReactElement, ReactNode, useEffect, useRef } from 'react';

import scrollIntoView from 'scroll-into-view-if-needed';

import { classnames, useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { MenuItem } from '@lumapps/lumx-menu/components/Menu/MenuItem';
import { ExpandableTreePanel } from '@lumapps/lumx-trees/components/ExpandableTreePanel';
import { SortableListNode } from '@lumapps/lumx-trees/components/SortableListNode';
import { mdiEye, mdiOpenInNew } from '@lumapps/lumx/icons';
import { FlexBox, Orientation, Placement, Tooltip } from '@lumapps/lumx/react';
import { isLinkNode, isSectionNode, NavigationItemType, NavigationMapItem } from '@lumapps/navigation/types';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { mergeRefs } from '@lumapps/utils/react/mergeRefs';
import { Direction } from '@lumapps/utils/types/Direction';

import { useNavigationAdminConfiguration } from '../../context/NavigationAdminConfigurationContext';
import { useNodeURL } from '../../hooks/useNodeURL';
import { NAVIGATION_ADMIN } from '../../keys';
import { canEditNode } from '../../utils';
import {
    getNavigationMapNodeTitle,
    nodeIsDeletable,
    nodeIsEditable,
    nodeIsHidden,
    nodeIsMovable,
} from '../../utils/nodes';
import { NavigationItemDetails } from '../NavigationItemDetails';

import './index.scss';

export interface NavigationListNodeProps {
    /** node to display */
    node: NavigationMapItem;
    /** Node's path */
    path: string[];
    /** callback to be executed once the node is deleted */
    onDeleteNode?: (path: string[], buttonRef: React.Ref<HTMLElement>) => void;
    /** callback to be executed once the node is edited */
    onEditNode?(path: string[], node: NavigationMapItem, buttonRef: React.Ref<HTMLElement>): void;
    /** callback to be executed once the node is moved in a specific direction */
    onMoveNode?(node: NavigationMapItem, direction: Direction): void;
    /** callback to be executed once the node will be moved to another section */
    onMoveNodeToSection?(node: NavigationMapItem, buttonRef: React.Ref<HTMLElement>): void;
    /** callback to be execute once the whole node is clicked. Needs `isHoverable=true` */
    onClickNode?(): void;
    /** children to display inside the navigation list */
    children?: ReactNode;
    /** custom actions to be displayed for the current node */
    actions?: ReactNode;
    /** Whether the preview button for content and links should be hidden */
    hidePreviewLink?: boolean;
    /** whether the expansion panel is disabled or not */
    disableExpansion?: boolean;
    /** custom class name */
    className?: string;
    /** whether this is the first node displayed on the page */
    isFirst?: boolean;
    /** whether this is the last node displayed on the page. */
    isLast?: boolean;
    /** Whether the node is inside a Section node */
    isInsideSection?: boolean;
    /** whether the node is disabled or not */
    isDisabled?: boolean;
    /** whether the node should display a hoverable state or not */
    isHoverable?: boolean;
    /** scope to be used for tracking purposes */
    scope: string;
    /** Maximum depth allowed for navigation items. */
    maxDepthAllowed?: number;
    /** List of root section ID's */
    rootSectionIds?: string[];
    /** True if the node is opened by default */
    open?: boolean;
    /** Whether the node should be displayed as filtered */
    showAsFiltered?: boolean;
    /** Whether the move arrows should be disabled */
    disableMoveArrows?: boolean;
    /** Whether the node should be hidden or not. */
    hide?: boolean;
}

const CLASSNAME = 'navigation-list-node';

export const NavigationListNode = React.forwardRef<HTMLDivElement, NavigationListNodeProps>(
    (
        {
            children,
            node,
            path,
            onDeleteNode,
            onEditNode,
            onMoveNodeToSection,
            disableExpansion = false,
            className,
            actions,
            hidePreviewLink = false,
            isFirst = false,
            isLast = false,
            isInsideSection = false,
            isDisabled = false,
            isHoverable = false,
            onClickNode,
            onMoveNode,
            scope,
            maxDepthAllowed,
            rootSectionIds,
            open = false,
            showAsFiltered,
            disableMoveArrows,
            hide,
        },
        ref,
    ) => {
        const { element, block } = useClassnames(CLASSNAME);
        const wrapperRef = useRef<HTMLDivElement>(null);
        const actionMenuRef = useRef<HTMLElement>(null);
        const { translateKey } = useTranslate();
        const { get } = useDataAttributes(scope);

        const [isOpen, setOpen] = React.useState(open);
        const { availableEditableNodeFields, isListNode } = useNavigationAdminConfiguration();
        const isSystemPage = node.type === NavigationItemType.SYSTEM_PAGE;

        // it is possible that the node is unavailable (resource has been deleted or not synced)
        // but system pages are an exception, having no title but a nodeTitle
        const isNodeUnavailable = node.title === undefined && !isSystemPage;

        const { linkAs, linkProps } = useNodeURL(node);

        // Manage css correctly depending on whether some elements are visible or not
        const onlyView = hidePreviewLink && !onDeleteNode && !onEditNode && !onMoveNode && !onMoveNodeToSection;
        const hiddenToggleStyles = onlyView ? { display: 'none' } : { visibility: 'hidden' };

        const expansionToggleStyles = isListNode?.(node) && !disableExpansion ? {} : hiddenToggleStyles;
        const isClickable = isHoverable && onClickNode;

        // Do not display the node if props hide, or if node partialPermission is 'none'
        const hideNode = hide || nodeIsHidden(node.partialPermission);
        /**
         *  Display Edit Action if:
         *  - node is available, AND
         *  - node has the field `visibility` in the availableEditableNodeFields, AND
         *  - no partialPermission, OR partialPermission is set to 'edit' or 'all'
         */
        const displayEdit =
            !isNodeUnavailable &&
            canEditNode(node, Boolean(availableEditableNodeFields?.visibility)) &&
            nodeIsEditable(node.partialPermission);

        /**
         *  Display Delete Action if:
         *  - no partialPermission, OR partialPermission is set to 'all'
         */
        const displayDelete = nodeIsDeletable(node.partialPermission);

        /**
         *  Display Move Action if:
         *  - no partialPermission, OR
         *  - partialPermission is set to 'all'
         */
        const displayMove = nodeIsMovable(node.partialPermission);

        /**
         * Determine if we display the move into another section button (respect all the rules) :
         *  1) Must displayMove
         *  2) for section with a max depth limit, the maxDepthAllowed should be superior to two in order to allow imbrication of sections.
         */
        const displayMoveToSection = displayMove && (!isSectionNode(node) || !maxDepthAllowed || maxDepthAllowed > 2);

        // If there is not at least one other section or node which accept children that is different from this node, we can't move it.
        const noOtherSections =
            !rootSectionIds?.length ||
            (rootSectionIds.length === 1 && node.id !== undefined && rootSectionIds.includes(node.id));

        /**
         * Determine if the Move to Section button should be disabled:
         * - Move to Section button should be displayed, AND
         * - If there is no other available section to move to OR if the node is inside a Section and the only node
         */
        const disableMoveToSection =
            displayMoveToSection && (noOtherSections || (isInsideSection && isFirst && isLast));

        /** Add tooltip on the Move to Section button for some cases where action is disabled */
        const addTooltipIfDisabled = (item: ReactElement) =>
            disableMoveToSection ? (
                <Tooltip
                    placement={Placement.LEFT}
                    label={
                        noOtherSections
                            ? translateKey(NAVIGATION_ADMIN.MOVE_DISABLED_NO_SECTION)
                            : translateKey(NAVIGATION_ADMIN.MOVE_DISABLED_ONLY_ELEMENT)
                    }
                >
                    {item}
                </Tooltip>
            ) : (
                item
            );

        useEffect(() => {
            const { current } = wrapperRef;
            if (open && current) {
                scrollIntoView(current, { scrollMode: 'if-needed' });
            }
        });

        if (hideNode) {
            return null;
        }

        /**
         * As the whole ExpandableTree has an onClick event, we need
         * to stop propagation to all inner actions.
         */
        const handleStopPropagation = (action?: () => void) => (e: React.SyntheticEvent) => {
            e.stopPropagation();
            if (action) {
                action();
            }
        };

        return (
            <ExpandableTreePanel
                scope={scope}
                aria-label={getNavigationMapNodeTitle(node)?.value}
                className={classnames(
                    block({
                        hidden: Boolean(hide),
                    }),
                    {
                        [`${className}`]: Boolean(className) && !isClickable,
                    },
                )}
                expansionPanelRef={mergeRefs([wrapperRef, ref])}
                isOpen={isOpen}
                setIsOpen={setOpen}
                expansionToggleStyles={expansionToggleStyles}
                level={path.length / 2}
                isHoverable={isHoverable}
                onClick={onClickNode}
                iconButtonRef={actionMenuRef}
                header={
                    <FlexBox
                        className={element('content', {
                            filtered: Boolean(showAsFiltered),
                        })}
                        orientation={Orientation.horizontal}
                    >
                        <SortableListNode<NavigationMapItem>
                            actions={actions}
                            additionalActions={
                                onMoveNodeToSection && displayMoveToSection
                                    ? addTooltipIfDisabled(
                                          <MenuItem
                                              isDisabled={disableMoveToSection}
                                              onClick={handleStopPropagation(() =>
                                                  onMoveNodeToSection(node, actionMenuRef),
                                              )}
                                              {...get({
                                                  element: 'item',
                                                  action: 'move',
                                              })}
                                          >
                                              {translateKey(GLOBAL.MOVE)}
                                          </MenuItem>,
                                      )
                                    : undefined
                            }
                            isFirst={isFirst}
                            isLast={isLast}
                            disableMoveDown={disableMoveArrows}
                            disableMoveUp={disableMoveArrows}
                            linkProps={
                                hidePreviewLink || !linkProps
                                    ? undefined
                                    : {
                                          linkAs,
                                          ...(linkProps as any),
                                          target: '_blank',
                                          label: translateKey(isLinkNode(node) ? GLOBAL.OPEN_NEW_TAB : GLOBAL.PREVIEW),
                                          icon: isLinkNode(node) ? mdiOpenInNew : mdiEye,
                                      }
                            }
                            node={node}
                            onDeleteNode={
                                displayDelete && onDeleteNode
                                    ? handleStopPropagation(() => onDeleteNode(path, actionMenuRef))
                                    : undefined
                            }
                            onEditNode={
                                displayEdit && onEditNode
                                    ? handleStopPropagation(() => onEditNode(path, node, actionMenuRef))
                                    : undefined
                            }
                            onMoveNode={
                                displayMove && onMoveNode
                                    ? (node, direction, event) =>
                                          handleStopPropagation(() => onMoveNode(node, direction))(event)
                                    : undefined
                            }
                            path={path}
                            scope={scope}
                            withStyles={false}
                            actionMenuProps={{
                                ref: actionMenuRef,
                                onClick: handleStopPropagation(),
                            }}
                        >
                            <div className={element('information')}>
                                <NavigationItemDetails
                                    node={node}
                                    isDisabled={isDisabled}
                                    isNodeUnavailable={isNodeUnavailable}
                                />
                            </div>
                        </SortableListNode>
                    </FlexBox>
                }
            >
                {isListNode?.(node) && isOpen ? children : null}
            </ExpandableTreePanel>
        );
    },
);

NavigationListNode.displayName = 'NavigationListNode';
